import { useEffect, useState } from 'react';
import axios from 'axios';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CardContent } from '@/components/ui/card';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const prettyDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : '';

/**
 * Deleting your own account. Required by the App Store for any app with sign-up, and decent
 * practice anyway. 60 days to change your mind, then the account is archived and the personal
 * details scrubbed.
 */
export const DeleteAccountCard = () => {
  const [state, setState] = useState(null);
  const [confirm, setConfirm] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/account/deletion`).then((r) => setState(r.data)).catch(() => {});
  }, []);

  const request = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/account/deletion`, { confirm, reason });
      setState({ ...state, ...r.data, pending: true });
      toast.success('Your account will close. You can undo this any time before then.');
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/account/deletion`);
      setState({ ...state, pending: false });
      toast.success('Welcome back — nothing was deleted.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  return (
    <CollapsibleCard
      title="Delete my account"
      icon={Trash2}
      testId="settings-delete-account"
      description="Close your account for good"
    >
      <CardContent className="space-y-4">
        {state.pending ? (
          <>
            <p className="text-sm text-gray-700" data-testid="deletion-pending-notice">
              Your account is set to close on <strong>{prettyDate(state.scheduled_for)}</strong>.
              Until then nothing has been removed, and you can change your mind.
            </p>
            <Button onClick={cancel} disabled={busy} data-testid="deletion-cancel-btn">
              Keep my account
            </Button>
          </>
        ) : (
          <>
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                You have <strong>{state.grace_days} days</strong> to change your mind. During that
                time your account is closed but nothing is removed — sign back in and you can undo
                it. After {state.grace_days} days your name, email and picture are erased and the
                account can no longer be used.
              </p>
              <p>
                Records we have to keep — orders, payments and tax records — stay in our books
                without your personal details attached, and anything you shared with a group stays
                with that group under a removed name.
              </p>
              <p>
                If you pay for a membership, cancel it first so you are not charged again.
              </p>
            </div>

            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Anything you would like us to know before you go? (optional)"
              rows={2}
              data-testid="deletion-reason-input"
            />

            <div className="space-y-2">
              <p className="text-sm text-gray-700">Type <strong>DELETE</strong> to confirm.</p>
              <Input
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="DELETE"
                className="max-w-[200px]"
                data-testid="deletion-confirm-input"
              />
            </div>

            <Button
              variant="destructive"
              onClick={request}
              disabled={busy || confirm.trim().toUpperCase() !== 'DELETE'}
              data-testid="deletion-request-btn"
            >
              Delete my account
            </Button>
          </>
        )}
      </CardContent>
    </CollapsibleCard>
  );
};

export default DeleteAccountCard;
